import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import * as ROUTES from '../../../lib/routing/frontend';
import { consultantDisplayName } from '../../../lib/helper/formatting';
import { patientDisplayName } from '../../../lib/helper/patientFilters';
import { patientConsultantSetNoShortfall } from '../../../lib/redux/actions/patients';
import { isSeniorStaff } from '../../../lib/helper/authorisation';
import { authByPath } from '../../../lib/routing/authorisation';

/**
 * MED-921 - which consultants have seen this person, and MED-1002's noShortfall beside each.
 *
 * Laid out as a `crud__section`, matching Linked Consultants on the hospital screen, so the two
 * places in the portal that answer "who is attached to this record" look and read the same.
 *
 * MED-921 asked for this panel to show `noShortfall` per consultant and could not: the flag lives
 * on `PatientConsultant`, and that entity was slice 2. This is slice 2.
 *
 * ## The Shortfalls column is senior staff and above
 *
 * Dan, 14/09/2026 - narrower than the screen it sits on, which every internal role reads. Setting
 * the flag stops a patient being told they owe money indefinitely, and the only trace is the
 * Suppressed Shortfalls screen, which is also senior staff and above. The decision and the audit
 * of it sit with the same people.
 *
 * Both halves are enforced: the API withholds `noShortfall` and `patientConsultantId` from the
 * patient payload below senior staff (`PatientAggregateProvider`), so this column would render as
 * "Not recorded" for a chaser even without the test below. The test is here so the column is
 * absent rather than misleading - "Not recorded" would tell a chaser that a suppressed patient was
 * not suppressed, which is worse than telling them nothing.
 *
 * ## The consultants are still derived from the invoices
 *
 * Not read from `patient_consultant`, even though that table now exists. The consultants this
 * person has been billed by is the fact the panel is about, `invoice.consultant_id` is NOT NULL on
 * every invoice in the estate, and reading the pair table instead would make a consultant vanish
 * off a patient's record if the backfill had not reached them. The pair rides along in each entry
 * so the flag can be shown and changed.
 *
 * ## Null is not false
 *
 * `noShortfall` comes back `null` where no pair has been recorded yet, and the cell then says so
 * rather than showing "Notified". MED-921 refused to ship this column at all rather than print
 * "No" for all 57,624 patients, because a suppression flag that looks settled when nobody has set
 * it is the one thing it must never do. That reasoning did not stop applying when the table
 * arrived - it narrowed to the rows the backfill has not reached.
 *
 * ## The View button asks the router, not a role list
 *
 * `CONSULTANTS.SINGLE` is `isStaff() || isConsultant()`, while this screen is open to every
 * internal role - so an outsourcer or a chaser could see a View button that lands them on
 * /access-denied. Offering somebody a button that refuses them is worse than not offering it.
 *
 * It asks `authByPath` rather than re-stating the roles here, so the button and the page it goes
 * to cannot drift: whoever changes the route's rule changes this at the same time, without
 * knowing this file exists. A second copy of `isStaff() || isConsultant()` is how they would
 * diverge.
 *
 * ## What the flag actually does, in the words on the screen
 *
 * It is narrower than pausing chasing, and the difference matters to whoever is about to click
 * it: only the shortfall notification is withheld, and only for this consultant. Ordinary
 * invoices for the same pair are chased exactly as before.
 */
class PatientConsultantsCard extends React.Component {
  constructor(props) {
    super(props);

    // Keyed per pair, so one slow request cannot grey out every switch in the table.
    this.state = { saving: {} };
  }

  toggleNoShortfall(consultant) {
    const { patientConsultantSetNoShortfall } = this.props;

    if (!consultant.patientConsultantId) {
      return;
    }

    this.setState((prev) => ({ saving: { ...prev.saving, [consultant.patientConsultantId]: true } }));

    Promise.resolve(patientConsultantSetNoShortfall(consultant.patientConsultantId, !consultant.noShortfall))
      // A rejection leaves the old value on screen, which is the honest outcome for a flag whose
      // whole purpose is to stop somebody being contacted: it must never look set when the save
      // did not land. The action throws a SubmissionError, so this catch is what stops an
      // unhandled rejection reaching the console.
      .catch(() => {})
      .finally(() => {
        this.setState((prev) => {
          const saving = { ...prev.saving };
          delete saving[consultant.patientConsultantId];

          return { saving };
        });
      });
  }

  renderNoShortfallCell(consultant) {
    const { saving } = this.state;

    if (consultant.patientConsultantId === null || typeof consultant.patientConsultantId === 'undefined') {
      return (
        <span className="text-muted" title="No patient/consultant record exists for this pair yet - one is created when their next invoice arrives.">
          Not recorded
        </span>
      );
    }

    const isSaving = Boolean(saving[consultant.patientConsultantId]);

    return (
      <button
        type="button"
        disabled={isSaving}
        className={`btn btn-sm ${consultant.noShortfall ? 'btn-warning' : 'btn-outline-secondary'}`}
        onClick={() => this.toggleNoShortfall(consultant)}
        title={
          consultant.noShortfall
            ? 'Shortfall notifications for this consultant are being withheld from this patient. Click to resume them.'
            : 'This patient is notified about shortfalls for this consultant. Click to withhold them and notify the admin address instead.'
        }>
        {isSaving && <i className="fas fa-spinner fa-spin mr-1" />}
        {consultant.noShortfall ? 'Suppressed' : 'Notified'}
      </button>
    );
  }

  render() {
    const { patient } = this.props;
    const consultants = patient.consultants || [];
    const canSeeShortfalls = isSeniorStaff();
    const canViewConsultants = authByPath(ROUTES.CONSULTANTS.SINGLE);

    if (consultants.length === 0) {
      return (
        <div className="crud__section mb-3 bg-white">
          <div className="crud__section-icon">
            <i className="fas fa-user-md" />
          </div>
          <h2 className="crud__section-header">Consultants</h2>
          <p className="text-muted mb-0">This patient has no invoices, so no consultant has seen them.</p>
        </div>
      );
    }

    return (
      <div className="crud__section mb-3 bg-white">
        <div className="crud__section-icon">
          <i className="fas fa-user-md" />
        </div>
        <h2 className="crud__section-header">Consultants</h2>
        <p className="text-muted mb-3">
          Consultants who have billed for {patientDisplayName(patient)}, derived from their invoices. &ldquo;Suppressed&rdquo; means shortfall notifications for
          that consultant are withheld from this patient and sent to the admin address instead - ordinary invoices are still chased as normal.
        </p>

        <div className="table-responsive">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Consultant</th>
                {canSeeShortfalls && <th className="text-center">Shortfalls</th>}
                {/* No column rather than an empty one - for a chaser this table is a plain list of names. */}
                {canViewConsultants && <th className="text-center">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {consultants.map((consultant) => (
                <tr key={consultant.id}>
                  <td>
                    <strong>{consultantDisplayName(consultant)}</strong>
                  </td>
                  {canSeeShortfalls && <td className="text-center">{this.renderNoShortfallCell(consultant)}</td>}
                  {canViewConsultants && (
                    <td className="text-center">
                      <Link
                        to={ROUTES.CONSULTANTS.SINGLE.replace(':id', consultant.id)}
                        className="btn btn-sm btn-outline-primary"
                        title={`View ${consultant.name}`}>
                        <i className="fas fa-eye mr-1" />
                        View
                      </Link>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

export default connect(null, { patientConsultantSetNoShortfall })(PatientConsultantsCard);
